import { Capacitor, type PluginListenerHandle } from "@capacitor/core";
import { PushNotifications, PushNotificationSchema } from "@capacitor/push-notifications";
import * as React from "react";
import { useHistory } from "react-router-dom";

import { sendEventToAppMeasurementTool, AppEventTokenEnum } from "@/utilities/capacitorLogic";
import { rememberIntent } from "@/utilities/intentNavigator";
import { classifyNotificationCategory } from "@/utilities/notificationClassification";

const allowUrl = (u?: string) => {
  if (!u) return false;
  try {
    const url = new URL(u);
    const ALLOW = ["propally.co.jp", "www.propally.co.jp", "propally.jp", "www.propally.jp"];
    return url.protocol === "https:" && ALLOW.includes(url.hostname);
  } catch {
    return false;
  }
};

const buildJournalPath = (data: any): string => {
  const route = data?.route ? String(data.route) : "/journal";
  const jurl = data?.journal_url ? String(data.journal_url) : undefined;
  if (route.startsWith("/journal") && allowUrl(jurl)) {
    const q = new URLSearchParams({ url: jurl! });
    return `/journal?${q.toString()}`;
  }
  return String(route);
};

const NotificationRouter: React.FC = () => {
  const history = useHistory();

  // Web版利用時は明示的に処理が行われないようにする
  const enabled = React.useMemo(() => {
    if (typeof window === "undefined") return false;
    try {
      return Capacitor.getPlatform() !== "web";
    } catch {
      return false;
    }
  }, []);

  React.useEffect(() => {
    if (!enabled) return; // webでは何もしない

    let sub1: PluginListenerHandle | undefined;

    (async () => {
      // 通知タップ時
      sub1 = await PushNotifications.addListener("pushNotificationActionPerformed", async (e) => {
        const dest = buildJournalPath(e.notification?.data);
        rememberIntent(dest);
        history.push(dest);

        // URLに応じて受信イベントを発火
        const n: PushNotificationSchema = e.notification;

        const rawBody = n.body ?? (n as any)?.notification?.body ?? "";
        const rawRoute = (n.data as any)?.route ?? "";
        const category = classifyNotificationCategory(rawRoute, rawBody);

        if (category === "journal") {
          sendEventToAppMeasurementTool(AppEventTokenEnum.NotificationJournalTap);
        } else if (category === "assessmentProposal") {
          sendEventToAppMeasurementTool(AppEventTokenEnum.NotificationAssessmentProposalTap);
        } else if (category === "investmentProposal") {
          sendEventToAppMeasurementTool(AppEventTokenEnum.NotificationInvestmentProposalTap);
        } else {
          // 意図しない通知のため計測しない
        }
      });
    })();

    return () => {
      sub1?.remove();
    };
  }, [history]);

  return null;
};

export default NotificationRouter;
